import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGoal } from '../context/GoalContext';
import Card from '../components/Card';
import Button from '../components/Button';
import ProgressBar from '../components/ProgressBar';
import AddMoneyModal from '../components/AddMoneyModal';
import Toast from '../components/Toast';
import { formatCurrency, formatDate, getMonthsRemaining, getMilestoneMessage } from '../utils/calculations';
import styles from './GoalDetailScreen.module.css';

const GoalDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getGoal, addMoney } = useGoal();

  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);

  // Show toast passed via navigate state (e.g. "Goal updated" from EditGoalScreen)
  useEffect(() => {
    if (location.state?.toast) {
      setToast({ message: location.state.toast });
      // Clear state so back-navigation doesn't re-show the toast
      window.history.replaceState({}, '');
    }
  }, [location.state?.toast]);
  // Store { amount, goalName } so the modal's close animation can finish before we apply state
  const [pending, setPending] = useState(null);

  const goal = getGoal(id);

  const handleAdd = useCallback((amount) => {
    // Called by modal when user taps Add — captures name at that moment
    setPending({ amount, goalName: goal ? goal.name : '' });
  }, [goal]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    if (pending) {
      addMoney(parseInt(id), pending.amount);
      setToast({ message: `Added ${formatCurrency(pending.amount)} to ${pending.goalName}` });
      setPending(null);
    }
  }, [pending, id, addMoney]);

  if (!goal) {
    return (
      <div className={styles.screen}>
        <div className={styles.notFound}>
          <p>Goal not found.</p>
          <button onClick={() => navigate('/')}>Go home</button>
        </div>
      </div>
    );
  }

  const pct = Math.min(100, Math.round((goal.currentSaved / goal.amount) * 100));
  const remaining = goal.amount - goal.currentSaved;
  const monthsLeft = getMonthsRemaining(goal.deadline);

  return (
    <div className={styles.screen}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="#1A1A1A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <span className={styles.title}>{goal.name}</span>
        <button className={styles.editBtn} onClick={() => navigate(`/goal/${id}/edit`)} aria-label="Edit goal">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M13.5 3.5L16.5 6.5M3 17H6L15.5 7.5L12.5 4.5L3 14V17Z" stroke="#1A1A1A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <div className={styles.content}>
        {/* Hero Card */}
        <Card className={styles.heroCard}>
          <div className={styles.heroTop}>
            <div>
              <p className={styles.savedLabel}>Saved so far</p>
              <h2 className={styles.savedAmount}>{formatCurrency(goal.currentSaved)}</h2>
            </div>
            <div className={styles.pctBadge}>{pct}%</div>
          </div>
          <ProgressBar current={goal.currentSaved} target={goal.amount} height={12} />
          <div className={styles.heroBottom}>
            <span className={styles.target}>Target: {formatCurrency(goal.amount)}</span>
            <span className={styles.remaining}>
              {remaining > 0 ? `${formatCurrency(remaining)} to go` : 'Goal reached!'}
            </span>
          </div>
          <div className={styles.milestone}>
            <span>{getMilestoneMessage(pct, goal.name, goal.currentSaved)}</span>
          </div>
        </Card>

        {/* Timeline Card */}
        <Card className={styles.timelineCard}>
          <div className={styles.timelineRow}>
            <div className={styles.timelineItem}>
              <p className={styles.timelineLabel}>Target date</p>
              <p className={styles.timelineValue}>{formatDate(goal.deadline)}</p>
            </div>
            <div className={styles.timelineDivider} />
            <div className={styles.timelineItem}>
              <p className={styles.timelineLabel}>Time left</p>
              <p className={styles.timelineValue}>{monthsLeft} months</p>
            </div>
            <div className={styles.timelineDivider} />
            <div className={styles.timelineItem}>
              <p className={styles.timelineLabel}>Status</p>
              <p className={`${styles.timelineValue} ${styles.onTrack}`}>On track</p>
            </div>
          </div>
        </Card>

        {/* Contributions */}
        {goal.contributions && goal.contributions.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Recent activity</h3>
            <Card>
              {goal.contributions.slice().reverse().slice(0, 5).map((c, i, arr) => (
                <div key={i} className={`${styles.contribution} ${i < arr.length - 1 ? styles.contributionBorder : ''}`}>
                  <div className={styles.contribLeft}>
                    <div className={styles.contribDot} />
                    <span className={styles.contribDate}>
                      {new Date(c.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <span className={styles.contribAmount}>+{formatCurrency(c.amount)}</span>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          label="Add money"
          onClick={() => setShowModal(true)}
          disabled={remaining <= 0}
        />
      </div>

      {showModal && (
        <AddMoneyModal
          goal={goal}
          onAdd={handleAdd}
          onClose={handleModalClose}
        />
      )}

      {toast && (
        <Toast message={toast.message} onDone={() => setToast(null)} />
      )}
    </div>
  );
};

export default GoalDetailScreen;
